/**
 * EPA correction request display helpers.
 *
 * `WasteManifestCorrectionRequest.sections` is an untyped JSON column holding
 * the regulator's own words. Everything that renders or exports that text goes
 * through here so the grid tooltip, the compliance card, and the discrepancy
 * export can never word the same request differently.
 *
 * Oak bundles encamp-shared from source, so this module must stay clear of
 * Prisma and Node built-ins — which is exactly why these helpers could not
 * stay beside the resolver that first needed them. Browser-safe imports are
 * fine.
 */
import { addUtcDays } from "../utils/date";

/**
 * The `ManifestDiscrepancy.field` sentinel for an EPA correction request.
 * Set by the discrepancy writer alongside the correction-request FK, and the
 * value every renderer keys on to treat a row as the regulator's finding.
 */
export const EPA_CORRECTION_REQUEST_FIELD = "epaCorrectionRequest";

/** One entry of a correction request's `sections`, flattened for display. */
export type EpaCorrectionSection = {
  sectionNumber: string;
  comments: string;
};

/**
 * Format one section the same way the drawer card title does
 * (`§ section – "comments"`), so list tooltips match detail.
 */
export function formatEpaCorrectionPreviewLine(section: {
  sectionNumber?: string | null;
  comments?: string | null;
}): string | null {
  const sectionNumber = section.sectionNumber?.trim() || null;
  const comments = section.comments?.trim() || null;
  if (!sectionNumber && !comments) return null;
  if (sectionNumber && comments) return `§ ${sectionNumber} – "${comments}"`;
  if (comments) return `"${comments}"`;
  return `§ ${sectionNumber}`;
}

/**
 * The first section carrying any text, formatted. Null when every section is
 * blank, which is what a caller with nothing worth showing falls back on.
 *
 * Skips blank leaders rather than reading index 0 — a request whose first
 * section is empty still has something to say further down the array.
 */
export function firstEpaCorrectionPreviewLine(
  sections: readonly { sectionNumber?: string | null; comments?: string | null }[]
): string | null {
  return (
    sections
      .map((section) => formatEpaCorrectionPreviewLine(section))
      .find((line): line is string => line != null) ?? null
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

/**
 * Parse the `sections` JSON into the label/comments pairs used for display.
 *
 * The column is untyped JSON, so every level is checked rather than asserted.
 * Anything that is not the expected shape reads as an empty string, and
 * `formatEpaCorrectionPreviewLine` drops a pair with neither field set.
 */
export function parseEpaCorrectionSections(raw: unknown): EpaCorrectionSection[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => {
    if (!isRecord(entry)) return { sectionNumber: "", comments: "" };
    const sectionNumber = isRecord(entry.sectionNumber) ? entry.sectionNumber : null;
    return {
      sectionNumber:
        readString(sectionNumber?.description) ?? readString(sectionNumber?.code) ?? "",
      comments: readString(entry.comments) ?? "",
    };
  });
}

/** EPA gives industry 30 days from a correction request to respond. */
export const EPA_CORRECTION_WINDOW_DAYS = 30;

/**
 * Response deadline for an EPA correction request: requestDate + the 30-day
 * EPA window, in UTC. Request dates are date-only values stored at UTC
 * midnight, so local-time arithmetic would drift the deadline a calendar day
 * in western timezones. Shared by every surface that shows or counts down to
 * this deadline (compliance cards, manifest detail footer, staff review
 * dialog, the discrepancy export) so they can never disagree.
 */
export function computeEpaCorrectionDeadline(requestDate: Date): Date {
  return addUtcDays(requestDate, EPA_CORRECTION_WINDOW_DAYS);
}
